// API functions for risks and risk sub-resources.
// Follows the same pattern as frameworkConfig.ts.

import type { ApiClient } from './client'

// --- Types ---

export interface Risk {
  id: string
  register_id: string
  name: string
  event_description: string
  drivers?: string | null
  impact_description?: string | null
  lifecycle_state: 'draft' | 'identify' | 'assess' | 'manage' | 'closed'
  taxonomy_node_id: string
  taxonomy_node_name: string
  taxonomy_node_path: string
  owner_role_id: string
  owner_user_id?: string | null
  owner_display_name?: string | null
  parent_risk_id?: string | null
  parent_risk_name?: string | null
  short_code: string
  created_via: string
  created_by: string
  residual_likelihood_level_id?: string | null
  inherent_likelihood_level_id?: string | null
  target_likelihood_level_id?: string | null
  residual_score?: number | null
  residual_likelihood?: number | null
  residual_max_impact?: number | null
  outside_appetite?: boolean | null
  taxonomy_version_stale: boolean
  linked_control_count: number
  linked_action_count: number
  linked_event_count: number
  child_risk_count: number
  has_overdue_assessments: boolean
  impact_assessments?: RiskImpactAssessmentSummary[]
  created_at: string
  updated_at: string
}

/** Impact assessment summary included in risk list responses. */
export interface RiskImpactAssessmentSummary {
  impact_table_id: string
  impact_table_name: string
  impact_level_id: string
  level_number: number
  score_type: 'residual' | 'inherent' | 'target'
}

export interface CreateRiskRequest {
  name: string
  event_description: string
  drivers?: string
  impact_description?: string
  taxonomy_node_id: string
  owner_role_id: string
}

export interface UpdateRiskRequest {
  name?: string
  event_description?: string
  drivers?: string
  impact_description?: string
  taxonomy_node_id?: string
  residual_likelihood_level_id?: string
  inherent_likelihood_level_id?: string
  target_likelihood_level_id?: string
  impact_assessments?: ImpactAssessmentInput[]
  change_notes?: string
}

export interface ImpactAssessmentInput {
  impact_table_id: string
  impact_level_id: string
  score_type: 'residual' | 'inherent' | 'target'
}

// --- Parent/Child types ---

export interface ChildRiskSummary {
  id: string
  name: string
  register_id: string
  register_name: string
  lifecycle_state: string
  residual_score?: number | null
  linked_at?: string | null
}

export interface ChildScoringComparison {
  risk_id: string
  name: string
  last_scoring_change?: string | null
  is_stale: boolean
}

export interface ChildScoringStatus {
  parent_last_scoring_change?: string | null
  children: ChildScoringComparison[]
  has_stale_children: boolean
}

export interface EligibleChild {
  id: string
  name: string
  register_id: string
  register_name: string
  lifecycle_state: string
  owner_display_name?: string | null
}

export interface MoveRiskRequest {
  dest_register_id: string
  change_notes?: string
}

export interface MoveRiskResponse {
  risk: Risk
  parent_severed: boolean
}

export interface TransitionRequest {
  change_notes?: string
}

// --- Risk list filters ---

export interface RiskListFilter {
  lifecycle_state?: string
  search?: string
  taxonomy_node_id?: string
}

// --- History types ---

export interface RiskHistoryEntry {
  id: string
  risk_id: string
  changed_at: string
  changed_by: string
  changed_by_name: string
  change_notes?: string | null
  lifecycle_state: string
  register_id: string
  taxonomy_node_id: string
  taxonomy_node_name: string
  taxonomy_node_path: string
  parent_risk_id?: string | null
  residual_likelihood_level_id?: string | null
  inherent_likelihood_level_id?: string | null
  target_likelihood_level_id?: string | null
  residual_likelihood_name?: string | null
  inherent_likelihood_name?: string | null
  target_likelihood_name?: string | null
  impact_assessments: HistoryImpactAssessment[]
}

export interface HistoryImpactAssessment {
  score_type: string
  impact_table_id: string
  impact_table_name: string
  impact_level_id: string
  impact_level_name: string
  level_number: number
}

// --- Linked controls (from risk side) ---

export interface LinkedControlSummary {
  link_id: string
  control_id: string
  control_name: string
  description?: string | null
  lifecycle_state: string
  category_id: string
  category_name: string
  owner_display_name?: string | null
  linked_at: string
  linked_by: string
  linked_by_name: string
}

// --- Linked actions (from risk side, via action_links) ---

export interface LinkedActionSummary {
  link_id: string
  action_id: string
  title: string
  description?: string | null
  lifecycle_state: string
  category_id: string
  category_name: string
  owner_display_name?: string | null
  due_date?: string | null
  is_overdue: boolean
  linked_at: string
  linked_by: string
  linked_by_name: string
}

// --- Linked events (from risk side, via event_risk_links) ---

export interface LinkedEventSummary {
  link_id: string
  event_id: string
  title: string
  description?: string | null
  lifecycle_state: string
  event_type_name: string
  category_id: string
  category_name: string
  event_date?: string | null
  recorded_by_name: string
  linked_at: string
  linked_by: string
  linked_by_name: string
}

// --- Lifecycle state helpers ---

export const lifecycleStates = ['draft', 'identify', 'assess', 'manage', 'closed'] as const

export const lifecycleStateLabels: Record<string, string> = {
  draft: 'Draft',
  identify: 'Identify',
  assess: 'Assess',
  manage: 'Manage',
  closed: 'Closed',
}

export const lifecycleStateColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  identify: 'bg-blue-100 text-blue-700',
  assess: 'bg-amber-100 text-amber-700',
  manage: 'bg-green-100 text-green-700',
  closed: 'bg-slate-100 text-slate-500',
}

// --- API functions ---

function buildFilterParams(filter?: RiskListFilter): string {
  if (!filter) return ''
  const params = new URLSearchParams()
  if (filter.lifecycle_state) params.set('lifecycle_state', filter.lifecycle_state)
  if (filter.search) params.set('search', filter.search)
  if (filter.taxonomy_node_id) params.set('taxonomy_node_id', filter.taxonomy_node_id)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export function risksApi(api: ApiClient) {
  return {
    // Risk CRUD (scoped to register for list/create)
    list: (registerId: string, filter?: RiskListFilter) =>
      api.get<Risk[]>(`/registers/${registerId}/risks${buildFilterParams(filter)}`),
    create: (registerId: string, data: CreateRiskRequest) =>
      api.post<Risk>(`/registers/${registerId}/risks`, data),
    get: (riskId: string) => api.get<Risk>(`/risks/${riskId}`),
    update: (riskId: string, data: UpdateRiskRequest) => api.put<Risk>(`/risks/${riskId}`, data),

    // State transitions
    identify: (riskId: string, data?: TransitionRequest) =>
      api.post<Risk>(`/risks/${riskId}/identify`, data),
    assess: (riskId: string, data?: TransitionRequest) =>
      api.post<Risk>(`/risks/${riskId}/assess`, data),
    manage: (riskId: string, data?: TransitionRequest) =>
      api.post<Risk>(`/risks/${riskId}/manage`, data),
    close: (riskId: string, data?: TransitionRequest) =>
      api.post<Risk>(`/risks/${riskId}/close`, data),
    reopen: (riskId: string, data?: TransitionRequest) =>
      api.post<Risk>(`/risks/${riskId}/reopen`, data),

    // Audit history
    history: (riskId: string) => api.get<RiskHistoryEntry[]>(`/risks/${riskId}/history`),

    // Cross-references
    controls: (riskId: string) => api.get<LinkedControlSummary[]>(`/risks/${riskId}/controls`),
    actions: (riskId: string) => api.get<LinkedActionSummary[]>(`/risks/${riskId}/actions`),
    events: (riskId: string) => api.get<LinkedEventSummary[]>(`/risks/${riskId}/events`),

    // Link/unlink controls (risk owns control_risk_links)
    linkControl: (riskId: string, controlId: string) =>
      api.post<{ link_id: string }>(`/risks/${riskId}/controls`, { control_id: controlId }),
    unlinkControl: async (riskId: string, linkId: string, notes?: string) => {
      const response = await api.fetch(`/risks/${riskId}/controls/${linkId}`, {
        method: 'DELETE',
        ...(notes ? { body: JSON.stringify({ notes }) } : {}),
      })
      return response.json() as Promise<{ status: string }>
    },

    // Parent/child relationships
    children: (riskId: string) =>
      api.get<ChildRiskSummary[]>(`/risks/${riskId}/children`),
    childScoringStatus: (riskId: string) =>
      api.get<ChildScoringStatus>(`/risks/${riskId}/child-scoring-status`),
    eligibleChildren: (riskId: string) =>
      api.get<EligibleChild[]>(`/risks/${riskId}/eligible-children`),
    linkChild: (riskId: string, childRiskId: string, changeNotes?: string) =>
      api.post<{ status: string }>(`/risks/${riskId}/children`, {
        child_risk_id: childRiskId,
        change_notes: changeNotes,
      }),
    unlinkChild: (riskId: string, childId: string, changeNotes?: string) =>
      api.post<{ status: string }>(`/risks/${riskId}/children/${childId}/unlink`, {
        change_notes: changeNotes,
      }),

    // Move to a different register
    move: (riskId: string, destRegisterId: string, changeNotes?: string) =>
      api.post<MoveRiskResponse>(`/risks/${riskId}/move`, {
        dest_register_id: destRegisterId,
        change_notes: changeNotes ?? '',
      }),
  }
}
